import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "diva_cart";

function CartFly({ fly, onDone }) {
  const reduce = useReducedMotion();
  if (reduce || !fly) return null;
  const targetX = window.innerWidth - 72;
  const targetY = 20;
  return (
    <motion.img
      src={fly.src}
      alt=""
      className="fixed z-[100] h-16 w-16 rounded-full object-cover shadow-xl pointer-events-none"
      initial={{ x: fly.x - 32, y: fly.y - 32, scale: 1, opacity: 1 }}
      animate={{ x: targetX, y: targetY, scale: 0.15, opacity: 0.4 }}
      transition={{ duration: 0.7, ease: [0.25, 0.8, 0.4, 1] }}
      onAnimationComplete={onDone}
    />
  );
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [fly, setFly] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, variant, qty = 1, fromRect = null) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.slug === product.slug && i.variant_id === variant.id
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(next[idx].qty + qty, 50) };
        return next;
      }
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          variant_id: variant.id,
          variant_label: variant.label,
          price: variant.price,
          qty,
        },
      ];
    });
    if (fromRect) {
      setFly({ src: product.image, x: fromRect.x + fromRect.width / 2, y: fromRect.y, id: Date.now() });
    }
  }, []);

  const removeItem = useCallback((slug, variantId) => {
    setItems((prev) => prev.filter((i) => !(i.slug === slug && i.variant_id === variantId)));
  }, []);

  const updateQty = useCallback((slug, variantId, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => !(i.slug === slug && i.variant_id === variantId))
        : prev.map((i) => (i.slug === slug && i.variant_id === variantId ? { ...i, qty } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((a, i) => a + i.qty, 0);
  const subtotal = items.reduce((a, i) => a + i.price * i.qty, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, count, subtotal, open, setOpen }}
    >
      {children}
      <AnimatePresence>
        {fly && <CartFly key={fly.id} fly={fly} onDone={() => setFly(null)} />}
      </AnimatePresence>
    </CartContext.Provider>
  );
}
